import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useApiService from 'services/api'
import { CompanyPageAccordion } from './CompanyPageAccordion'
import { DealCard } from './DealCard'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import CreateDealModal from 'components/Deals/CreateDealModal/CreateDealModal'
import { DealsModal } from './Modals/DealsModal/DealsModal'
import { Deal } from 'models/deals'
import { TableTypeSlug, CompanyTypeSlug } from 'models/companies'
import { PiArrowRight } from 'react-icons/pi'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

interface DealsOverviewAccordionProps {
  chainProxyId?: number
  doorProxyId?: number
  tableTypeSlug: TableTypeSlug
  companyTypeSlug: CompanyTypeSlug
  isChainPage?: boolean
}

export function DealsOverviewAccordion({
  chainProxyId,
  doorProxyId,
  tableTypeSlug,
  companyTypeSlug,
  isChainPage = false,
}: DealsOverviewAccordionProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { dealDetailPage } = useFeatureFlagService()
  const [dealIdToEdit, setDealIdToEdit] = useState<number>()
  const [showCreateDeal, setShowCreateDeal] = useState(false)
  const [showDealsModal, setShowDealsModal] = useState(false)

  // Determine which company ID to use
  const contactCompanyId = chainProxyId || doorProxyId

  // Fetch contact company data for the modal
  const { data: contactCompany } = api.useGetCompany(contactCompanyId as number)

  const params = {
    ...(doorProxyId
      ? { door_proxy_id: doorProxyId, door_only: true }
      : { company: contactCompanyId }),
    page_size: 50,
    include_ltv: true,
  }

  // Fetch deals for the company or door
  // When on a door page, only show door-specific deals (not company-wide deals)
  // Include LTV data for company/door page views (predicted revenue/volume)
  const [{ data: dealsResponse, isLoading: dealsLoading }, queryKey] =
    api.useGetDeals(params)

  // Fetch sales stages
  const { data: salesStages } = api.useGetSalesStages()

  // Fetch users for account owner names
  const { data: users } = api.useGetUsers()

  const deals = dealsResponse?.results || []

  // Handle deal edit
  const handleEditDeal = (deal: Deal) => {
    setDealIdToEdit(deal.id)
  }

  const invalidateDealsData = () => {
    queryClient.invalidateQueries({ queryKey: queryKey })
    if (chainProxyId) {
      queryClient.invalidateQueries({ queryKey: ['company', chainProxyId] })
    }
    if (doorProxyId) {
      queryClient.invalidateQueries({ queryKey: ['door_proxy', doorProxyId] })
    }
  }

  const handleDealCreated = () => {
    setShowCreateDeal(false)
    invalidateDealsData()
  }

  return (
    <>
      <CompanyPageAccordion
        title={`DEALS (${deals.length})`}
        defaultOpen={true}
        disableToggle={deals.length === 0}
        headerRight={
          <div className="flex items-center gap-3">
            {/* All Deals button - using ActionButton pattern from CompanyTopCard */}
            {isChainPage && (
              <button
                onClick={() => setShowDealsModal(true)}
                className="flex items-center justify-center gap-2 px-3 py-[6px] rounded-md border-[1px] border-brand-hover bg-surface-hover h-8 hover:bg-surface-hover-deep transition-colors"
              >
                <span className="font-medium text-sm leading-none text-brand">
                  All Deals
                </span>
                <span className="font-light text-sm leading-none text-center text-brand">
                  ({deals.length})
                </span>
              </button>
            )}

            {/* Add deal — plain text link, no button chrome. */}
            <button
              type="button"
              onClick={() => setShowCreateDeal(true)}
              className="text-base font-medium text-brand-bright hover:text-brand-active transition-colors"
            >
              Add a deal
            </button>
          </div>
        }
      >
        {dealsLoading ? (
          <div className="text-center py-8 text-gray-500">Loading deals...</div>
        ) : deals.length === 0 ? null : (
          <StageColorProvider value={dealDetailPage ? 'pipeline' : 'win_rate'}>
            <div className="relative">
              {/* Carousel/Scrollable container */}
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-4 min-w-max">
                  {deals.map((deal) => (
                    <div key={deal.id} className="flex-shrink-0">
                      <DealCard
                        deal={deal}
                        salesStages={salesStages}
                        users={users}
                        onEdit={handleEditDeal}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Scroll hint if there are multiple deals */}
              {deals.length > 1 && (
                <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-2">
                  <span>Scroll horizontally to see all deals</span>
                  <PiArrowRight className="w-3.5 h-3.5" />
                </div>
              )}
            </div>
          </StageColorProvider>
        )}
      </CompanyPageAccordion>

      {/* Edit Deal Modal */}
      <EditDealModal
        onClose={() => setDealIdToEdit(undefined)}
        dealsTableQueryKey={queryKey}
        show={!!dealIdToEdit}
        dealId={dealIdToEdit}
      />

      {/* Create Deal Modal */}
      {showCreateDeal && (
        <CreateDealModal
          show={showCreateDeal}
          handleClose={() => setShowCreateDeal(false)}
          targetCompanyId={chainProxyId}
          targetDoorProxyId={doorProxyId}
          forDoorSelection={!!doorProxyId}
          tableTypeSlug={tableTypeSlug}
          companyTypeSlug={companyTypeSlug}
          totalSelectedRows={1}
          optimisticUiTarget="deals-section"
          onDealCreated={handleDealCreated}
        />
      )}

      {/* Deals Modal - shows all deals in a table */}
      {contactCompany && (
        <DealsModal
          show={showDealsModal}
          onHide={() => setShowDealsModal(false)}
          contactCompany={contactCompany}
          forDoorProxyId={doorProxyId}
          companyName={contactCompany.name}
        />
      )}
    </>
  )
}
